import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { adminMatchesAPI } from '../services/adminApi';

const AdminScoreModal = ({
  isOpen,
  onClose,
  match,
  onSuccess,
}) => {
  const [scores, setScores] = useState({
    team1_set1: '',
    team1_set2: '',
    team1_set3: '',
    team2_set1: '',
    team2_set2: '',
    team2_set3: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const matchState = match?.status || 'scheduled';
  const isScheduled = matchState === 'scheduled';
  const isPending = matchState === 'score_submitted';
  const isCompleted = matchState === 'completed';
  const isDisputed = matchState === 'disputed';
  const hasScore = isPending || isCompleted || isDisputed;
  const canEditScores = isScheduled || isCompleted || isDisputed;
  const canConfirm = isPending;
  const canClear = hasScore;

  useEffect(() => {
    if (isOpen && match) {
      const s1 = match.team1_score_set1;
      const s2 = match.team1_score_set2;
      const s3 = match.team1_score_set3;
      const o1 = match.team2_score_set1;
      const o2 = match.team2_score_set2;
      const o3 = match.team2_score_set3;

      setScores({
        team1_set1: s1 != null ? String(s1) : '',
        team1_set2: s2 != null ? String(s2) : '',
        team1_set3: s3 != null ? String(s3) : '',
        team2_set1: o1 != null ? String(o1) : '',
        team2_set2: o2 != null ? String(o2) : '',
        team2_set3: o3 != null ? String(o3) : '',
      });
      setError(null);
    }
  }, [isOpen, match]);

  const isMatchTied = useMemo(() => {
    const t1_1 = parseInt(scores.team1_set1, 10) || 0;
    const t1_2 = parseInt(scores.team1_set2, 10) || 0;
    const t2_1 = parseInt(scores.team2_set1, 10) || 0;
    const t2_2 = parseInt(scores.team2_set2, 10) || 0;
    if (scores.team1_set1 === '' || scores.team1_set2 === '' || scores.team2_set1 === '' || scores.team2_set2 === '') {
      return false;
    }
    let team1Sets = 0, team2Sets = 0;
    if (t1_1 > t2_1) team1Sets++;
    else if (t2_1 > t1_1) team2Sets++;
    if (t1_2 > t2_2) team1Sets++;
    else if (t2_2 > t1_2) team2Sets++;
    return team1Sets === 1 && team2Sets === 1;
  }, [scores.team1_set1, scores.team1_set2, scores.team2_set1, scores.team2_set2]);

  const handleScoreChange = (set, team, value) => {
    if (!canEditScores) return;
    if (set === 3 && !isMatchTied) return;
    if (value === '' || (value >= 0 && value <= 30)) {
      setScores((prev) => {
        const next = { ...prev, [`team${team}_set${set}`]: value };
        if ((set === 1 || set === 2) && value !== '') {
          const t1_1 = set === 1 && team === 1 ? parseInt(value, 10) : parseInt(next.team1_set1, 10) || 0;
          const t1_2 = set === 2 && team === 1 ? parseInt(value, 10) : parseInt(next.team1_set2, 10) || 0;
          const t2_1 = set === 1 && team === 2 ? parseInt(value, 10) : parseInt(next.team2_set1, 10) || 0;
          const t2_2 = set === 2 && team === 2 ? parseInt(value, 10) : parseInt(next.team2_set2, 10) || 0;
          let team1Sets = 0, team2Sets = 0;
          if (t1_1 > t2_1) team1Sets++;
          else if (t2_1 > t1_1) team2Sets++;
          if (t1_2 > t2_2) team1Sets++;
          else if (t2_2 > t1_2) team2Sets++;
          if (!(team1Sets === 1 && team2Sets === 1)) {
            next.team1_set3 = '';
            next.team2_set3 = '';
          }
        }
        return next;
      });
    }
  };

  const validateScores = () => {
    if (scores.team1_set1 === '' || scores.team1_set2 === '' || scores.team2_set1 === '' || scores.team2_set2 === '') {
      setError('Please fill in all set scores');
      return false;
    }
    const vals = [
      scores.team1_set1, scores.team1_set2, scores.team1_set3,
      scores.team2_set1, scores.team2_set2, scores.team2_set3,
    ].filter((s) => s !== '').map((s) => parseInt(s, 10));
    if (vals.some((v) => isNaN(v) || v < 0 || v > 30)) {
      setError('Please enter valid scores (0–30)');
      return false;
    }
    const t1_1 = parseInt(scores.team1_set1, 10);
    const t1_2 = parseInt(scores.team1_set2, 10);
    const t2_1 = parseInt(scores.team2_set1, 10);
    const t2_2 = parseInt(scores.team2_set2, 10);
    let team1Sets = 0, team2Sets = 0;
    if (t1_1 > t2_1) team1Sets++;
    else if (t2_1 > t1_1) team2Sets++;
    if (t1_2 > t2_2) team1Sets++;
    else if (t2_2 > t1_2) team2Sets++;
    if (isMatchTied && scores.team1_set3 !== '' && scores.team2_set3 !== '') {
      const t1_3 = parseInt(scores.team1_set3, 10);
      const t2_3 = parseInt(scores.team2_set3, 10);
      if (t1_3 > t2_3) team1Sets++;
      else if (t2_3 > t1_3) team2Sets++;
    }
    if (team1Sets < 2 && team2Sets < 2) {
      setError('One team must win at least 2 sets');
      return false;
    }
    setError(null);
    return true;
  };

  const scoresToPayload = () => {
    const p = { ...scores };
    if (!isMatchTied) {
      p.team1_set3 = '';
      p.team2_set3 = '';
    }
    return p;
  };

  const handleSave = async () => {
    if (!canEditScores || !match?.id) return;
    if (!validateScores()) return;
    setLoading(true);
    setError(null);
    try {
      await adminMatchesAPI.putScore(match.id, scoresToPayload());
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save score');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!canConfirm || !match?.id) return;
    setLoading(true);
    setError(null);
    try {
      await adminMatchesAPI.confirmScore(match.id);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to confirm score');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    if (!canClear || !match?.id) return;
    if (!window.confirm('Clear this score and revert match to scheduled? Standings will be recalculated.')) return;
    setLoading(true);
    setError(null);
    try {
      await adminMatchesAPI.deleteScore(match.id);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to clear score');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const statusLabel = isScheduled ? 'Scheduled' : isPending ? 'Pending' : isCompleted ? 'Completed' : isDisputed ? 'Disputed' : matchState;

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <HeaderContent>
            <Title>Admin – Score</Title>
            <Subtitle>{match?.team1_name} vs {match?.team2_name}</Subtitle>
            <StatusBadge $status={matchState}>{statusLabel}</StatusBadge>
          </HeaderContent>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <FormContainer>
          <ScoreboardSection>
            <SetHeaders>
              <SetHeader>Team</SetHeader>
              <SetHeader>Set 1</SetHeader>
              <SetHeader>Set 2</SetHeader>
              <SetHeader>
                Tie Breaker
                {!isMatchTied && canEditScores && scores.team1_set1 !== '' && scores.team2_set1 !== '' && (
                  <LockHint>🔒</LockHint>
                )}
              </SetHeader>
            </SetHeaders>

            <TeamRow>
              <TeamName>{match?.team1_name}</TeamName>
              <ScoreInput
                type="number"
                min="0"
                max="30"
                value={scores.team1_set1}
                onChange={(e) => handleScoreChange(1, 1, e.target.value)}
                readOnly={!canEditScores}
              />
              <ScoreInput
                type="number"
                min="0"
                max="30"
                value={scores.team1_set2}
                onChange={(e) => handleScoreChange(2, 1, e.target.value)}
                readOnly={!canEditScores}
              />
              <ScoreInput
                type="number"
                min="0"
                max="30"
                value={scores.team1_set3}
                onChange={(e) => handleScoreChange(3, 1, e.target.value)}
                readOnly={!canEditScores || !isMatchTied}
                disabled={!isMatchTied}
                $locked={!isMatchTied}
              />
            </TeamRow>

            <TeamRow>
              <TeamName>{match?.team2_name}</TeamName>
              <ScoreInput
                type="number"
                min="0"
                max="30"
                value={scores.team2_set1}
                onChange={(e) => handleScoreChange(1, 2, e.target.value)}
                readOnly={!canEditScores}
              />
              <ScoreInput
                type="number"
                min="0"
                max="30"
                value={scores.team2_set2}
                onChange={(e) => handleScoreChange(2, 2, e.target.value)}
                readOnly={!canEditScores}
              />
              <ScoreInput
                type="number"
                min="0"
                max="30"
                value={scores.team2_set3}
                onChange={(e) => handleScoreChange(3, 2, e.target.value)}
                readOnly={!canEditScores || !isMatchTied}
                disabled={!isMatchTied}
                $locked={!isMatchTied}
              />
            </TeamRow>
          </ScoreboardSection>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <ButtonContainer>
            {canEditScores && (isScheduled || isCompleted || isDisputed) && (
              <SaveButton onClick={handleSave} disabled={loading}>
                {loading ? 'Saving...' : isScheduled ? 'Save score' : 'Save changes'}
              </SaveButton>
            )}
            {canConfirm && (
              <ConfirmButton onClick={handleConfirm} disabled={loading}>
                {loading ? 'Confirming...' : 'Confirm score'}
              </ConfirmButton>
            )}
            {canClear && (
              <ClearButton onClick={handleClear} disabled={loading}>
                {loading ? 'Clearing...' : 'Clear score'}
              </ClearButton>
            )}
          </ButtonContainer>
        </FormContainer>
      </ModalContainer>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 24px;
  border-bottom: 2px solid #e0e0e0;
  background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
  color: white;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Title = styled.h2`
  margin: 0 0 8px 0;
  font-size: 22px;
  font-weight: 700;
`;

const Subtitle = styled.div`
  font-size: 16px;
  opacity: 0.95;
  margin-bottom: 8px;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${(p) =>
    p.$status === 'completed' ? 'rgba(34, 197, 94, 0.3)' :
    p.$status === 'score_submitted' ? 'rgba(245, 158, 11, 0.3)' :
    p.$status === 'disputed' ? 'rgba(239, 68, 68, 0.3)' :
    'rgba(255,255,255,0.2)'};
`;

const LockHint = styled.span`
  margin-left: 4px;
  font-size: 12px;
  opacity: 0.8;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 28px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  padding: 0;
  margin-left: 12px;
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const FormContainer = styled.div`
  padding: 24px;
`;

const ScoreboardSection = styled.div`
  margin-bottom: 20px;
`;

const SetHeaders = styled.div`
  display: grid;
  grid-template-columns: 1fr 70px 70px 70px;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
`;

const SetHeader = styled.div`
  font-weight: 600;
  color: #374151;
  font-size: 13px;
  text-align: center;
`;

const TeamRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 70px 70px 70px;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
`;

const TeamName = styled.div`
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ScoreInput = styled.input`
  width: 70px;
  height: 48px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  background: ${(p) => (p.$locked ? '#f3f4f6' : 'white')};
  opacity: ${(p) => (p.$locked ? 0.7 : 1)};
  cursor: ${(p) => (p.readOnly && p.$locked ? 'not-allowed' : 'text')};
  &:focus {
    outline: none;
    border-color: #043663;
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  color: #dc2626;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SaveButton = styled.button`
  background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ConfirmButton = styled.button`
  background: #059669;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #047857;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ClearButton = styled.button`
  background: transparent;
  color: #dc2626;
  border: 2px solid #dc2626;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #fef2f2;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default AdminScoreModal;
